import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmpresaTenant, GrupoEconomico, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import AvisoEmpresasOrfas from './AvisoEmpresasOrfas';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaNovoGrupoEconomico, justificativaVinculoEmpresaGrupo } from './justificativaSox';
import { formatarCnpj } from '../../utils/String';

const scopeTag = (s: 'ATIVO' | 'INATIVO') =>
  <span className={`wf-badge ${s === 'ATIVO' ? 'wf-badge--aprovada' : 'wf-badge--bloqueada'}`}>{s}</span>;

const GruposEconomicosPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 1;

  const [grupos, setGrupos] = useState<GrupoEconomico[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioWorkflow[]>([]);
  const [orfas, setOrfas] = useState<EmpresaTenant[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedGrupo, setSelectedGrupo] = useState<GrupoEconomico | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showNovoGrupoModal, setShowNovoGrupoModal] = useState(false);
  const [toast, setToast] = useState('');

  const [novoGrupoNome, setNovoGrupoNome] = useState('');
  const [criandoGrupo, setCriandoGrupo] = useState(false);

  const [vinculoEmpresaId, setVinculoEmpresaId] = useState<number | ''>('');
  const [vinculoGrupoId, setVinculoGrupoId] = useState<number | ''>('');
  const [salvandoVinculo, setSalvandoVinculo] = useState(false);
  // Justificativas SOX já sugeridas (citam empresa/grupo escolhidos); a pessoa revisa e edita antes de confirmar.
  const justificativa = useJustificativaSugerida(justificativaVinculoEmpresaGrupo({
    empresa: empresas.find(e => e.id === vinculoEmpresaId)?.nome,
    grupo: grupos.find(g => g.grupo_id === vinculoGrupoId)?.nome,
  }));
  const novoGrupoJust = useJustificativaSugerida(justificativaNovoGrupoEconomico(novoGrupoNome));
  const [vinculoErro, setVinculoErro] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const fetchDados = () => {
    setLoading(true);
    Promise.all([
      workflowService.listarGrupos(),
      workflowService.listarTenants(),
      workflowService.listarUsuarios(),
      workflowService.listarEmpresasOrfas(),
    ])
      .then(([g, e, u, o]) => {
        setGrupos(g);
        setEmpresas(e);
        setUsuarios(u);
        setOrfas(o);
        if (g.length > 0 && !selectedGrupo) setSelectedGrupo(g[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const salvarVinculo = async () => {
    if (!justificativa.texto.trim() || vinculoEmpresaId === '' || vinculoGrupoId === '') return;
    setVinculoErro('');
    setSalvandoVinculo(true);
    try {
      await workflowService.vincularTenantGrupo(Number(vinculoGrupoId), Number(vinculoEmpresaId), justificativa.texto.trim());
      setShowModal(false);
      justificativa.restaurar();
      setVinculoEmpresaId('');
      setVinculoGrupoId('');
      fetchDados();
      showToast('Registro de auditoria SOX gerado com sucesso. Vínculo empresa-grupo persistido.');
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data;
      if (status === 403) {
        setVinculoErro('Acesso negado: usuário não cadastrado no módulo Workflow SOX. Vá em Usuários → Editar e marque como "Administrador TI".');
      } else if (status === 404) {
        setVinculoErro(typeof msg === 'string' ? msg : 'Empresa não encontrada no sistema.');
      } else {
        setVinculoErro(typeof msg === 'string' ? msg : 'Erro ao salvar. Tente novamente.');
      }
    } finally {
      setSalvandoVinculo(false);
    }
  };

  const criarGrupo = () => {
    if (!novoGrupoNome.trim()) return;
    setCriandoGrupo(true);
    workflowService
      .criarGrupo({ nome: novoGrupoNome.trim(), justificativaSox: novoGrupoJust.texto.trim() || undefined })
      .then(g => {
        setShowNovoGrupoModal(false);
        setNovoGrupoNome('');
        novoGrupoJust.restaurar();
        fetchDados();
        setSelectedGrupo(g);
        showToast(`Grupo "${g.nome}" criado. Log SOX registrado.`);
      })
      .catch(() => {})
      .finally(() => setCriandoGrupo(false));
  };

  return <div className='wf-page'>
    <div className='wf-breadcrumb'>Home &rsaquo; Grupos Econômicos</div>
    <div className='wf-header'>
      <div><h1>Grupos Econômicos</h1><p>Estrutura hierárquica de grupos, empresas vinculadas e rastreabilidade SOX.</p></div>
      <div className='wf-header__actions'>
        {fromOnboarding && (
          <button
            className='wf-btn wf-btn--secondary'
            onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}
          >
            ← Onboarding Etapa {onboardingEtapa}
          </button>
        )}
        {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
        <span className='wf-level7'>Acesso Nível 7 — TI</span>
        <button className='wf-btn wf-btn--secondary' onClick={() => { setVinculoEmpresaId(''); setVinculoGrupoId(''); justificativa.restaurar(); setVinculoErro(''); setShowModal(true); }}>Registrar vínculo</button>
        <button className='wf-btn wf-btn--primary' onClick={() => { setNovoGrupoNome(''); novoGrupoJust.restaurar(); setShowNovoGrupoModal(true); }}>+ Novo grupo</button>
      </div>
    </div>

    {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

    <AvisoEmpresasOrfas
      empresas={orfas}
      orientacao='Estas empresas estão sem grupo ou responsável vinculado. Regularize o acesso e insira a justificativa SOX.'
    />

    {loading ? (
      <p className='wf-count' style={{ textAlign: 'center', padding: 32 }}>Carregando...</p>
    ) : (
      <div className='wf-ge-layout'>
        <aside>
          <section className='wf-card'>
            <div className='wf-card__header'><div><h2>Grupos cadastrados</h2><p>{grupos.length} grupo(s)</p></div></div>
            <div className='wf-ge-list'>
              {grupos.length === 0 && <p className='wf-count' style={{ padding: '12px 16px', color: '#a1a5b7' }}>Nenhum grupo cadastrado.</p>}
              {grupos.map(g => <button key={g.grupo_id} className={`wf-ge-item ${selectedGrupo?.grupo_id === g.grupo_id ? 'is-active' : ''}`} onClick={() => setSelectedGrupo(g)}>
                <div>
                  <strong>{g.nome}</strong>
                  <span>{empresas.filter(e => g.tenant_ids.includes(e.id)).length} empresa(s)</span>
                </div>
                {scopeTag(g.status)}
              </button>)}
            </div>
          </section>
        </aside>

        <main>
          {selectedGrupo && <>
            <section className='wf-card'>
              <div className='wf-card__header'>
                <div><h2>{selectedGrupo.nome}</h2><p>Responsável: {selectedGrupo.responsavel}</p></div>
                {scopeTag(selectedGrupo.status)}
              </div>
              <div className='wf-table-wrap'><table className='wf-table'>
                <thead><tr><th>Empresa</th><th>CNPJ</th><th>Responsável</th><th>Status</th><th>Vínculo SOX</th></tr></thead>
                <tbody>
                  {empresas.filter(e => selectedGrupo.tenant_ids.includes(e.id)).length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#a1a5b7', padding: 16 }}>Nenhuma empresa vinculada a este grupo.</td></tr>
                  ) : empresas.filter(e => selectedGrupo.tenant_ids.includes(e.id)).map(emp => {
                    const resp = usuarios.find(u => u.id === emp.responsavelId);
                    return <tr key={emp.id}>
                      <td><strong>{emp.nome}</strong></td>
                      <td>{formatarCnpj(emp.cnpj)}</td>
                      <td>{resp ? `${resp.nome} — ${resp.cargo}` : <span className='wf-danger-text'>Sem responsável</span>}</td>
                      <td><span className={`wf-badge ${emp.status === 'ATIVA' ? 'wf-badge--aprovada' : emp.status === 'ORFA' ? 'wf-badge--rejeitada' : 'wf-badge--bloqueada'}`}>{emp.status}</span></td>
                      <td><span className='wf-ge-hash'>Vinculado ✓</span></td>
                    </tr>;
                  })}
                </tbody>
              </table></div>
            </section>
          </>}
          {!selectedGrupo && !loading && (
            <section className='wf-card'>
              <p className='wf-count' style={{ textAlign: 'center', padding: 32, color: '#a1a5b7' }}>Selecione um grupo à esquerda para ver os detalhes.</p>
            </section>
          )}
        </main>
      </div>
    )}

    {/* Modal registrar vínculo */}
    {showModal && <div className='wf-modal-bg'><div className='wf-modal wf-modal--wide'>
      <div className='wf-modal__header'><div><h2>Registrar vínculo empresa-grupo</h2><p>Toda alteração gera log imutável no histórico SOX.</p></div><button onClick={() => setShowModal(false)}>×</button></div>
      <div className='wf-modal__body'>
        <div className='wf-form-grid'>
          <label>Empresa
            <select value={vinculoEmpresaId} onChange={e => setVinculoEmpresaId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value=''>— Selecione a empresa —</option>
              {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </label>
          <label>Grupo econômico
            <select value={vinculoGrupoId} onChange={e => setVinculoGrupoId(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value=''>— Selecione o grupo —</option>
              {grupos.map(g => <option key={g.grupo_id} value={g.grupo_id}>{g.nome}</option>)}
            </select>
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 600, color: '#5e6278' }}>
          Justificativa SOX <span style={{ color: '#f64e60', fontWeight: 400 }}>(obrigatória)</span>
          <CampoJustificativaSox justificativa={justificativa} rows={4} placeholder='Descreva o motivo do vínculo para fins de auditoria SOX...' />
        </label>
        {justificativa.texto.length > 0 && <div className='wf-sod'><b>✓</b><div><strong>Registro SOX será gerado automaticamente</strong><p>Campos capturados: quem, quando, quê, justificativa e hash de integridade.</p></div></div>}
        {vinculoErro && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff5f8', border: '1px solid #f1416c', borderRadius: 8, fontSize: 13, color: '#f1416c', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, fontWeight: 700 }}>✗</span>
            <span>{vinculoErro}</span>
          </div>
        )}
      </div>
      <div className='wf-modal__footer'>
        <button className='wf-btn wf-btn--secondary' onClick={() => setShowModal(false)} disabled={salvandoVinculo}>Cancelar</button>
        <button
          className='wf-btn wf-btn--primary'
          disabled={!justificativa.texto.trim() || vinculoEmpresaId === '' || vinculoGrupoId === '' || salvandoVinculo}
          onClick={salvarVinculo}
        >
          {salvandoVinculo ? 'Salvando...' : 'Salvar e registrar SOX'}
        </button>
      </div>
    </div></div>}

    {/* Modal novo grupo */}
    {showNovoGrupoModal && <div className='wf-modal-bg'><div className='wf-modal'>
      <div className='wf-modal__header'><div><h2>Novo grupo econômico</h2><p>Um log SOX será registrado automaticamente.</p></div><button onClick={() => setShowNovoGrupoModal(false)}>×</button></div>
      <div className='wf-modal__body'>
        <div className='wf-form-grid wf-form-grid--one'>
          <label>Nome do grupo *
            <input value={novoGrupoNome} onChange={e => setNovoGrupoNome(e.target.value)} placeholder='Ex: Grupo Holding Brasil' />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 600, color: '#5e6278' }}>
            Justificativa SOX
            <CampoJustificativaSox justificativa={novoGrupoJust} rows={3} placeholder='Motivo da criação do grupo para fins de auditoria SOX...' />
          </label>
        </div>
      </div>
      <div className='wf-modal__footer'>
        <button className='wf-btn wf-btn--secondary' onClick={() => setShowNovoGrupoModal(false)}>Cancelar</button>
        <button className='wf-btn wf-btn--primary' disabled={!novoGrupoNome.trim() || criandoGrupo} onClick={criarGrupo}>
          {criandoGrupo ? 'Criando...' : 'Criar grupo'}
        </button>
      </div>
    </div></div>}
  </div>;
};

export default GruposEconomicosPage;
